// Posts a lead to Zoho CRM's Web-to-Lead endpoint.
//
// Zoho's endpoint answers a POST with a 302 and does not support a CORS
// preflight for form posts, so fetch/XHR cannot be used: the browser blocks the
// redirect and the submission is lost. The workaround Zoho's own embed relies on
// is a real <form> POST targeted at a hidden iframe — the navigation happens
// inside the iframe, the page never leaves, and the lead reaches Zoho.
//
// Because the response is opaque to us (cross-origin iframe), a successful
// resolve means "the browser posted it", not "Zoho accepted it". Zoho validates
// server-side; watch the CRM for the first few real submissions.

const ZOHO_ACTION = "https://crm.zoho.in/crm/WebToLeadForm";

// Identifies the RIS web form ("RIS Web Form"). From the Zoho-generated embed
// (refreshed 2026-10-20) - do not edit. If the form is changed in Zoho, re-read
// these values and the field names below from its embed script.
const ZOHO_IDS = {
  xnQsjsdp: "918a3ab5d105e5a4dabdd0e758aba44e3ba646c2dbec030e3ded61f424b7125d",
  xmIwtLD:
    "21fec5733de8bcc03a4f1e6e923fea9564f6747b4a27a0d71d961cd68ceda0923684761688b617297a12edaeb34f6b21",
  actionType: "TGVhZHM=",
};

const IFRAME_NAME = "ris-zoho-sink";

function ensureSink() {
  let frame = document.getElementById(IFRAME_NAME);
  if (!frame) {
    frame = document.createElement("iframe");
    frame.id = IFRAME_NAME;
    frame.name = IFRAME_NAME;
    frame.setAttribute("aria-hidden", "true");
    frame.setAttribute("tabindex", "-1");
    frame.style.cssText = "position:absolute;width:0;height:0;border:0;left:-9999px;";
    document.body.appendChild(frame);
  }
  return frame;
}

// Maps our field names onto the web form's fields. Zoho silently drops any field
// that is not on the form, so these names must match the embed exactly:
//   Last Name -> "Name", LEADCF16 -> "What are you interested in?",
//   LEADCF17 -> "Message (optional)". The form has no Description field.
function toZohoFields(lead) {
  const fields = {
    ...ZOHO_IDS,
    returnURL: "null",
    "Last Name": lead.name,
    Email: lead.email,
    Mobile: lead.phone,
    LEADCF17: buildDescription(lead),
  };
  if (lead.interest) fields.LEADCF16 = lead.interest;
  if (lead.utm_source) fields["Lead Source"] = lead.utm_source;
  return fields;
}

// The form has no fields for page/campaign/calculator context, so it is folded
// into the Message field (LEADCF17), where it stays visible on the lead record.
function buildDescription(lead) {
  const lines = [];
  if (lead.message) lines.push(lead.message, "");
  if (lead.interest) lines.push(`Interest: ${lead.interest}`);
  if (lead.audience) lines.push(`Audience: ${lead.audience}`);
  if (lead.calculator) lines.push(`Calculator: ${lead.calculator}`);
  if (lead.calculator_result) lines.push(`Calculator result: ${lead.calculator_result}`);
  if (lead.source_page) lines.push(`Page: ${lead.source_page}`);
  const utm = ["utm_source", "utm_medium", "utm_campaign"]
    .map((k) => (lead[k] ? `${k}=${lead[k]}` : null))
    .filter(Boolean);
  if (utm.length) lines.push(`Campaign: ${utm.join(" · ")}`);
  if (lead.consent_text) lines.push("", `Consent given: "${lead.consent_text} Privacy Policy."`);
  return lines.join("\n");
}

export function submitLeadToZoho(lead) {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("No document"));
      return;
    }

    const frame = ensureSink();
    const form = document.createElement("form");
    form.method = "POST";
    form.action = ZOHO_ACTION;
    form.target = IFRAME_NAME;
    form.acceptCharset = "UTF-8";
    form.style.display = "none";

    const fields = toZohoFields(lead);
    Object.entries(fields).forEach(([name, value]) => {
      if (value === undefined || value === null || value === "") return;
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = String(value);
      form.appendChild(input);
    });

    let settled = false;
    const cleanup = () => {
      frame.removeEventListener("load", onLoad);
      clearTimeout(timer);
      if (form.parentNode) form.parentNode.removeChild(form);
    };
    const onLoad = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };
    // The iframe load event is the only signal available cross-origin. If it
    // never fires (blocked, offline), fail rather than leave the UI spinning.
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error("Zoho submission timed out"));
    }, 15000);

    frame.addEventListener("load", onLoad);
    document.body.appendChild(form);
    form.submit();
  });
}
